import { useQueryClient } from "@tanstack/react-query"
import { Select } from "antd"
import { DashboardContainer, H6 } from "components"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAppNotificationStore } from "stores"
import { useGet } from "utils/useFetch"
import { surveyTypes } from "../Survey"
import DeleteSurvey from "../modals/DeleteSurvey"
import { ColumnsType } from "antd/es/table"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"

const Surveys = () => {
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const navigate = useNavigate()
  const [typeId, setTypeId] = useState("1")
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [activeDeleteId, setActiveDeleteId] = useState("")

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [ADMIN_PEOPLE_ENDPOINTS.SELECT_SURVEYS, typeId],
    })
  }

  const {
    isLoading: isLoadingSurvey,
    isPreviousData,
    data: surveyData,
    tablePagination,
  } = useBackendTableQuery<IPaginatedRes<ISurvey>>({
    queryKeyBase: [ADMIN_PEOPLE_ENDPOINTS.SELECT_SURVEYS, typeId],
    queryFn: (params) =>
      adminPeopleService.selectSurveys({ typeId, ...params }),
    getTotal: (data) => data?.data?.totalDocs ?? 0,
    searchParamName: "searchParams",
    initialPageSize: 20,
  })

  const TableColumns: ColumnsType<ISurvey> = [
    {
      title: "Description",
      dataIndex: "description",
      render: (_, record: ISurvey) => <span>{record.description}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      filters: [
        { text: "New", value: "1" },
        { text: "Ready", value: "2" },
      ],
      onFilter: (
        value: boolean | string | number | bigint,
        record: ISurvey
      ) => {
        return record?.survey_status_id === Number(value)
      },
      render: (_, record: ISurvey) => (
        <span
          className={`text-sm font-medium py-1 block text-center w-[80px] rounded-[4px]
                    ${
                      record?.survey_status_id === 2
                        ? "bg-success100 text-success800"
                        : ""
                    }
                    ${
                      record?.survey_status_id === 1
                        ? "bg-neutral40 text-neutral500"
                        : ""
                    }
                    ${
                      record?.survey_status_id === 3
                        ? "bg-[#FEE3B1] text-[#E69702]"
                        : ""
                    }

                    `}
        >
          {record?.survey_status}
        </span>
      ),
    },

    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (_, record: ISurvey) => (
        <TableActionSelect
          items={actionItems({
            id: record.survey_id,
            key: record.survey_key,
            statusId: record?.survey_status_id,
            typeId: record.survey_type_id,
          })}
        />
      ),
    },
  ]

  //  three dots on each row
  const actionItems = (data: {
    key: string
    id: number
    statusId: number
    typeId: number
  }) => {
    let arrayOptions: any = []
    {
      data?.statusId === 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "0",
            label: (
              <Link to={`/dashboard/survey/preview?x=${data?.key}`}>
                Preview
              </Link>
            ),
          },
        ])
    }
    {
      data?.statusId === 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "1",
            label: (
              <Link to={`/dashboard/survey/edit?x=${data?.key}`}>Edit</Link>
            ),
          },
        ])
    }

    {
      data?.statusId !== 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "2",
            label: (
              <div
                onClick={() => {
                  navigate(`/dashboard/survey/edit?x=${data?.key}`)
                }}
              >
                Finish
              </div>
            ),
          },
        ])
    }
    {
      data?.statusId === 2 &&
        (data?.typeId === 1 || data?.typeId === 2) &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "3",
            label: (
              <div
                onClick={() => {
                  navigate(`/dashboard/survey/assign?x=${data?.key}`)
                }}
              >
                Assign
              </div>
            ),
          },
        ])
    }
    {
      data?.statusId &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "4",
            label: (
              <div
                onClick={() => {
                  handleOpenDeleteModal(String(data.id))
                }}
              >
                Delete
              </div>
            ),
          },
        ])
    }
    {
      data?.statusId === 2 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "7",
            label: (
              <Link to={`/dashboard/survey/view-responses?x=${data?.key}`}>
                View Responses
              </Link>
            ),
          },
        ])
    }

    return arrayOptions
  }
  const handleOpenDeleteModal = (id: string) => {
    setOpenDeleteConfirmation(true)
    setActiveDeleteId(String(id))
  }

  return (
    <div className="relative !font-avenir text-[#42526D]">
      <DashboardContainer>
        <div className="flex flex-col h-full">
          <div className="flex justify-end items-center mb-4">
            <Select
              defaultValue={typeId}
              style={{ width: 125 }}
              placeholder="Filter by"
              onChange={(value: string) => {
                setTypeId(value)
              }}
              options={surveyTypes.map((surv) => ({
                value: surv.survey_id,
                label: surv.survey_title,
              }))}
              className="font-avenir "
            />
          </div>
          <AppTable
            scroll={false}
            columns={TableColumns}
            dataSource={surveyData?.data?.docs || []}
            rowKey="survey_id"
            loading={isLoadingSurvey || isPreviousData}
            className="text-[#42526D] min-h-screen"
            pagination={tablePagination}
          />
        </div>
      </DashboardContainer>
      <DeleteSurvey
        title="Delete Survey"
        description="  If you delete this survey, it would be removed from your list of
          surveys. Are you sure you want to continue?"
        deleteFn={adminPeopleService.removeSurvey}
        activeDeleteId={activeDeleteId}
        open={openDeleteConfirmation}
        onClose={() => {
          setOpenDeleteConfirmation(false)
          setActiveDeleteId("")
        }}
        invalidateQueries={invalidateQueries}
      />
    </div>
  )
}
export default Surveys

export interface ISurvey {
  survey_id: number
  survey_key: string
  survey_status_id: number
  survey_status: string
  survey_type_id: number
  survey_type: null
  description: string
  is_active: boolean
}
